// Definición de cada reporte: endpoint, hojas del Excel y columnas.
// La vista ReporteView y el exportador a Excel se arman a partir de esta configuración, así
// que agregar un reporte nuevo es agregar una entrada aquí, su ruta y su opción en el menú.

export type TipoColumna = 'texto' | 'entero' | 'numero' | 'moneda' | 'porcentaje';

export type TotalColumna =
    | 'suma'
    | 'ultimo'
    // Se calcula a partir de los totales ya sumados del resto de columnas (p. ej. el margen).
    | ((totales: Record<string, number>) => number);

export interface ColumnaReporte {
    key: string;
    header: string;
    width: number;
    tipo: TipoColumna;
    total?: TotalColumna;
}

export interface HojaReporte {
    key: string;         // clave en respuesta.datos
    nombre: string;      // nombre de la pestaña y de la hoja Excel (máx. 31 caracteres)
    columnas: ColumnaReporte[];
    soloConMes?: boolean; // el backend solo la llena al filtrar por mes o día
}

export interface ConfigReporte {
    titulo: string;
    icono: string;
    endpoint: string;
    archivo: string;
    descripcion: string;
    hojas: HojaReporte[];
}

const ratio = (numerador: string, denominador: string) => (t: Record<string, number>) =>
    t[denominador] ? t[numerador] / t[denominador] : 0;

const COLUMNA_PERIODO: ColumnaReporte = { key: 'periodo', header: 'Período', width: 18, tipo: 'texto' };

export const REPORTES: Record<string, ConfigReporte> = {
    'ventas-mes': {
        titulo: 'Ventas por mes',
        icono: 'fa-chart-line',
        endpoint: '/reportes/ventas-mes',
        archivo: 'ventas',
        descripcion: 'Ventas agrupadas por mes (gestión), por día (mes) o por hora (día), y su distribución por tipo de pago.',
        hojas: [
            {
                key: 'periodos',
                nombre: 'Ventas por período',
                columnas: [
                    COLUMNA_PERIODO,
                    { key: 'num_ventas',      header: 'N° Ventas',       width: 12, tipo: 'entero', total: 'suma' },
                    { key: 'unidades',        header: 'Unidades',        width: 12, tipo: 'numero', total: 'suma' },
                    { key: 'total_vendido',   header: 'Total Vendido',   width: 16, tipo: 'moneda', total: 'suma' },
                    { key: 'ticket_promedio', header: 'Ticket Promedio', width: 16, tipo: 'moneda', total: ratio('total_vendido', 'num_ventas') },
                ],
            },
            {
                key: 'tipos_pago',
                nombre: 'Por tipo de pago',
                columnas: [
                    { key: 'tipo_pago',     header: 'Tipo de Pago',  width: 30, tipo: 'texto' },
                    { key: 'num_ventas',    header: 'N° Ventas',     width: 12, tipo: 'entero',     total: 'suma' },
                    { key: 'total_vendido', header: 'Total Vendido', width: 16, tipo: 'moneda',     total: 'suma' },
                    { key: 'participacion', header: 'Participación', width: 14, tipo: 'porcentaje', total: 'suma' },
                ],
            },
        ],
    },
    'compras': {
        titulo: 'Reporte de compras',
        icono: 'fa-truck',
        endpoint: '/reportes/compras',
        archivo: 'compras',
        descripcion: 'Compras registradas en el período, su detalle de productos y el resumen por proveedor.',
        hojas: [
            {
                key: 'compras',
                nombre: 'Compras',
                columnas: [
                    { key: 'id_compra',    header: 'N° Compra',    width: 11, tipo: 'texto' },
                    { key: 'fecha_compra', header: 'Fecha',        width: 17, tipo: 'texto' },
                    { key: 'proveedor',    header: 'Proveedor',    width: 30, tipo: 'texto' },
                    { key: 'nit',          header: 'NIT',          width: 14, tipo: 'texto' },
                    { key: 'tipo_pago',    header: 'Tipo de Pago', width: 22, tipo: 'texto' },
                    { key: 'items',        header: 'Ítems',        width: 8,  tipo: 'entero', total: 'suma' },
                    { key: 'total',        header: 'Total',        width: 14, tipo: 'moneda', total: 'suma' },
                ],
            },
            {
                key: 'detalle',
                nombre: 'Detalle de compras',
                columnas: [
                    { key: 'id_compra',     header: 'N° Compra',     width: 11, tipo: 'texto' },
                    { key: 'fecha_compra',  header: 'Fecha',         width: 17, tipo: 'texto' },
                    { key: 'proveedor',     header: 'Proveedor',     width: 26, tipo: 'texto' },
                    { key: 'codigo_barras', header: 'Código',        width: 16, tipo: 'texto' },
                    { key: 'producto',      header: 'Producto',      width: 35, tipo: 'texto' },
                    { key: 'cantidad',      header: 'Cantidad',      width: 11, tipo: 'numero', total: 'suma' },
                    { key: 'precio_compra', header: 'Precio Compra', width: 14, tipo: 'moneda' },
                    { key: 'importe',       header: 'Importe',       width: 14, tipo: 'moneda', total: 'suma' },
                ],
            },
            {
                key: 'proveedores',
                nombre: 'Por proveedor',
                columnas: [
                    { key: 'proveedor',     header: 'Proveedor',     width: 32, tipo: 'texto' },
                    { key: 'nit',           header: 'NIT',           width: 14, tipo: 'texto' },
                    { key: 'num_compras',   header: 'N° Compras',    width: 12, tipo: 'entero',     total: 'suma' },
                    { key: 'total',         header: 'Total',         width: 14, tipo: 'moneda',     total: 'suma' },
                    { key: 'participacion', header: 'Participación', width: 14, tipo: 'porcentaje', total: 'suma' },
                ],
            },
        ],
    },
    'productos-vendidos': {
        titulo: 'Productos vendidos',
        icono: 'fa-box-open',
        endpoint: '/reportes/productos-vendidos',
        archivo: 'productos_vendidos',
        descripcion: 'Productos vendidos con su costo y ganancia. El detalle por ítem de venta se incluye al filtrar por mes o día.',
        hojas: [
            {
                key: 'productos',
                nombre: 'Por producto',
                columnas: [
                    { key: 'codigo_barras',   header: 'Código',          width: 16, tipo: 'texto' },
                    { key: 'producto',        header: 'Producto',        width: 35, tipo: 'texto' },
                    { key: 'categoria',       header: 'Categoría',       width: 20, tipo: 'texto' },
                    { key: 'marca',           header: 'Marca',           width: 16, tipo: 'texto' },
                    { key: 'cantidad',        header: 'Cantidad',        width: 11, tipo: 'numero',     total: 'suma' },
                    { key: 'precio_promedio', header: 'P. Venta Prom.',  width: 14, tipo: 'moneda' },
                    { key: 'costo_promedio',  header: 'Costo Prom.',     width: 13, tipo: 'moneda' },
                    { key: 'total_venta',     header: 'Total Venta',     width: 14, tipo: 'moneda',     total: 'suma' },
                    { key: 'costo_total',     header: 'Costo Total',     width: 14, tipo: 'moneda',     total: 'suma' },
                    { key: 'ganancia',        header: 'Ganancia',        width: 14, tipo: 'moneda',     total: 'suma' },
                    { key: 'margen',          header: 'Margen',          width: 10, tipo: 'porcentaje', total: ratio('ganancia', 'total_venta') },
                ],
            },
            {
                key: 'detalle',
                nombre: 'Detalle por ítem',
                soloConMes: true,
                columnas: [
                    { key: 'fecha_venta',     header: 'Fecha',          width: 17, tipo: 'texto' },
                    { key: 'tipo_pago',       header: 'Tipo de Pago',   width: 24, tipo: 'texto' },
                    { key: 'producto',        header: 'Producto',       width: 35, tipo: 'texto' },
                    { key: 'cantidad',        header: 'Cantidad',       width: 11, tipo: 'numero',     total: 'suma' },
                    { key: 'precio_unitario', header: 'P. Unitario',    width: 12, tipo: 'moneda' },
                    { key: 'descuento',       header: 'Descuento',      width: 11, tipo: 'moneda' },
                    { key: 'importe',         header: 'Importe',        width: 13, tipo: 'moneda',     total: 'suma' },
                    { key: 'costo_unitario',  header: 'Costo Unit.',    width: 12, tipo: 'moneda' },
                    { key: 'costo_total',     header: 'Costo Total',    width: 13, tipo: 'moneda',     total: 'suma' },
                    { key: 'ganancia',        header: 'Ganancia',       width: 13, tipo: 'moneda',     total: 'suma' },
                    { key: 'margen',          header: 'Margen',         width: 10, tipo: 'porcentaje', total: ratio('ganancia', 'importe') },
                ],
            },
        ],
    },
    'utilidad': {
        titulo: 'Ingresos y utilidad',
        icono: 'fa-chart-pie',
        endpoint: '/reportes/utilidad',
        archivo: 'ingresos_utilidad',
        descripcion: 'Ingresos por ventas, costo de lo vendido (PEPS) y utilidad bruta por período y por categoría.',
        hojas: [
            {
                key: 'periodos',
                nombre: 'Por período',
                columnas: [
                    COLUMNA_PERIODO,
                    { key: 'ingresos',       header: 'Ingresos',        width: 16, tipo: 'moneda',     total: 'suma' },
                    { key: 'costo_ventas',   header: 'Costo de Ventas', width: 16, tipo: 'moneda',     total: 'suma' },
                    { key: 'utilidad_bruta', header: 'Utilidad Bruta',  width: 16, tipo: 'moneda',     total: 'suma' },
                    { key: 'margen',         header: 'Margen',          width: 10, tipo: 'porcentaje', total: ratio('utilidad_bruta', 'ingresos') },
                ],
            },
            {
                key: 'categorias',
                nombre: 'Por categoría',
                columnas: [
                    { key: 'categoria',      header: 'Categoría',       width: 28, tipo: 'texto' },
                    { key: 'ingresos',       header: 'Ingresos',        width: 16, tipo: 'moneda',     total: 'suma' },
                    { key: 'costo_ventas',   header: 'Costo de Ventas', width: 16, tipo: 'moneda',     total: 'suma' },
                    { key: 'utilidad_bruta', header: 'Utilidad Bruta',  width: 16, tipo: 'moneda',     total: 'suma' },
                    { key: 'margen',         header: 'Margen',          width: 10, tipo: 'porcentaje', total: ratio('utilidad_bruta', 'ingresos') },
                ],
            },
        ],
    },
    'caja': {
        titulo: 'Movimiento de caja',
        icono: 'fa-cash-register',
        endpoint: '/reportes/caja',
        archivo: 'movimiento_caja',
        descripcion: 'Entradas (ventas) y salidas (compras) con saldo acumulado desde el inicio del período. Los movimientos uno a uno se incluyen al filtrar por mes o día.',
        hojas: [
            {
                key: 'periodos',
                nombre: 'Resumen',
                columnas: [
                    COLUMNA_PERIODO,
                    { key: 'num_ventas',  header: 'N° Ventas',       width: 11, tipo: 'entero', total: 'suma' },
                    { key: 'ingresos',    header: 'Ingresos',        width: 15, tipo: 'moneda', total: 'suma' },
                    { key: 'num_compras', header: 'N° Compras',      width: 11, tipo: 'entero', total: 'suma' },
                    { key: 'egresos',     header: 'Egresos',         width: 15, tipo: 'moneda', total: 'suma' },
                    { key: 'neto',        header: 'Neto',            width: 15, tipo: 'moneda', total: 'suma' },
                    { key: 'saldo',       header: 'Saldo Acumulado', width: 16, tipo: 'moneda', total: 'ultimo' },
                ],
            },
            {
                key: 'tipos_pago',
                nombre: 'Por tipo de pago',
                columnas: [
                    { key: 'tipo_pago', header: 'Tipo de Pago', width: 30, tipo: 'texto' },
                    { key: 'ingresos',  header: 'Ingresos',     width: 15, tipo: 'moneda', total: 'suma' },
                    { key: 'egresos',   header: 'Egresos',      width: 15, tipo: 'moneda', total: 'suma' },
                    { key: 'neto',      header: 'Neto',         width: 15, tipo: 'moneda', total: 'suma' },
                ],
            },
            {
                key: 'movimientos',
                nombre: 'Movimientos',
                soloConMes: true,
                columnas: [
                    { key: 'fecha',     header: 'Fecha',        width: 17, tipo: 'texto' },
                    { key: 'tipo',      header: 'Tipo',         width: 10, tipo: 'texto' },
                    { key: 'documento', header: 'Documento',    width: 18, tipo: 'texto' },
                    { key: 'detalle',   header: 'Cliente / Proveedor', width: 30, tipo: 'texto' },
                    { key: 'tipo_pago', header: 'Tipo de Pago', width: 24, tipo: 'texto' },
                    { key: 'entrada',   header: 'Entrada',      width: 14, tipo: 'moneda', total: 'suma' },
                    { key: 'salida',    header: 'Salida',       width: 14, tipo: 'moneda', total: 'suma' },
                    { key: 'saldo',     header: 'Saldo',        width: 14, tipo: 'moneda', total: 'ultimo' },
                ],
            },
        ],
    },
};

export const FORMATO_NUMERAL: Record<TipoColumna, string> = {
    texto: '',
    entero: '0,0',
    numero: '0,0.00',
    moneda: '0,0.00',
    porcentaje: '0.00%',
};

export const FORMATO_EXCEL: Record<TipoColumna, string | undefined> = {
    texto: undefined,
    entero: '#,##0',
    numero: '#,##0.00',
    moneda: '#,##0.00',
    porcentaje: '0.00%',
};

// pg devuelve los numeric como string: los pasamos a número para ordenar, totalizar y exportar.
export const normalizarFilas = (hoja: HojaReporte, filas: any[]) => {
    const numericas = hoja.columnas.filter((c) => c.tipo !== 'texto').map((c) => c.key);
    return filas.map((fila) => {
        const copia = { ...fila };
        numericas.forEach((key) => { copia[key] = Number(copia[key] ?? 0); });
        return copia;
    });
};

export const calcularTotales = (hoja: HojaReporte, filas: any[]) => {
    const totales: Record<string, number> = {};

    hoja.columnas.forEach((c) => {
        if (c.total === 'suma') {
            const suma = filas.reduce((acc, fila) => acc + Number(fila[c.key] || 0), 0);
            totales[c.key] = Math.round(suma * 100) / 100;
        } else if (c.total === 'ultimo') {
            totales[c.key] = filas.length ? Number(filas[filas.length - 1][c.key] || 0) : 0;
        }
    });
    hoja.columnas.forEach((c) => {
        if (typeof c.total === 'function') totales[c.key] = c.total(totales);
    });

    return totales;
};
